import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { PageHeader, Card, Badge, Modal, Btn, Field, inputCls } from "../components/common";
import { fmtIDR, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { CheckCircle2, Clock, UserCircle, RefreshCw, Smartphone, Upload, Landmark, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STAGE_LABEL = { booking: "Booking", berkas: "Kelengkapan Berkas", ke_bank: "Berkas ke Bank", approved_bank: "Approve Bank", spk: "SPK Terbit", akad_notice: "Pemberitahuan Akad", dp_lunas: "DP Lunas", akad: "Akad", serah_terima: "Serah Terima", selesai: "Selesai" };
const BERKAS_LABEL = { ktp_pemohon: "KTP Pemohon", ktp_pasangan: "KTP Pasangan", kk: "Kartu Keluarga", npwp: "NPWP" };
const BERKAS_APP_LABEL = { ktp: "KTP", ktp_pasangan: "KTP Pasangan", kk: "Kartu Keluarga", npwp: "NPWP", skk: "Surat Ket. Kerja", slip_gaji: "Slip Gaji 3 bln", rekening_koran: "Rekening Koran 3 bln", skp_desa: "SK Penghasilan Desa", sku_desa: "SK Usaha Desa" };

export default function Konsumen() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [tab, setTab] = useState("spasi");
  const [appUsers, setAppUsers] = useState([]);
  const [pulling, setPulling] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [auDetail, setAuDetail] = useState(null);
  const [auEdit, setAuEdit] = useState(null);
  const [auForm, setAuForm] = useState({});
  const { user } = useAuth();
  const isSA = user?.role === "Super Admin";
  const uploadRef = { current: {} };

  const load = useCallback(() => { api.get("/konsumen").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const loadApp = useCallback(() => { api.get("/ajl/app-users").then((r) => setAppUsers(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); loadApp(); }, [load, loadApp]);

  const pullApp = async () => {
    setPulling(true);
    try { const r = await api.post("/ajl/pull"); toast.success(r.data.skipped ? "URL aplikasi AJL belum diset" : "Penarikan selesai"); loadApp(); }
    catch (e) { toast.error(errMsg(e)); } finally { setPulling(false); }
  };

  const makeLead = (au) => api.post(`/ajl/app-users/${au.id}/make-lead`)
    .then((r) => toast.success(r.data.message)).catch((e) => toast.error(errMsg(e)));

  const uploadBerkas = async (bid, code, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("code", code); fd.append("file", file);
    try { await api.post(`/sales/bookings/${bid}/berkas-app`, fd); toast.success("Berkas terunggah sebagai sales"); openDetail(bid); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const requireRK = (bid) => api.post(`/sales/bookings/${bid}/require-rk`)
    .then((r) => { toast.success(r.data.message); openDetail(bid); })
    .catch((e) => toast.error(errMsg(e)));

  const openEdit = () => {
    setEditForm({
      customer_name: detail.booking?.customer_name || "", customer_phone: detail.booking?.customer_phone || "",
      nik: detail.form?.nik || "", tempat_lahir: detail.form?.tempat_lahir || "", tanggal_lahir: detail.form?.tanggal_lahir || "",
      alamat: detail.form?.alamat || "", no_hp: detail.form?.no_hp || "", pekerjaan: detail.form?.pekerjaan || "",
    });
    setEditOpen(true);
  };

  const saveEdit = () => api.put(`/konsumen/${detail.booking.id}`, editForm)
    .then((r) => { toast.success(r.data.message); setEditOpen(false); openDetail(detail.booking.id); load(); })
    .catch((e) => toast.error(errMsg(e)));

  const deleteKonsumen = () => {
    if (!window.confirm(`Hapus data konsumen ${detail.booking?.customer_name} (${detail.booking?.number})? Unit akan kembali tersedia dan akun konsumen ikut terhapus.`)) return;
    api.delete(`/konsumen/${detail.booking.id}`)
      .then((r) => { toast.success(r.data.message); setDetail(null); load(); })
      .catch((e) => toast.error(errMsg(e)));
  };

  const deleteBerkas = (bid, code) => {
    if (!window.confirm(`Hapus berkas ${code.replace(/_/g, " ").toUpperCase()}? Konsumen dapat mengunggah ulang.`)) return;
    api.delete(`/sales/bookings/${bid}/berkas-app/${code}`)
      .then((r) => { toast.success(r.data.message); openDetail(bid); })
      .catch((e) => toast.error(errMsg(e)));
  };

  const openAuEdit = (au) => {
    setAuForm({ name: au.name || "", phone: au.phone || "", email: au.email || "", address: au.address || "",
      city: au.city || "", occupation: au.occupation || "", job_type: au.job_type || "", marital_status: au.marital_status || "",
      birth_place: au.birth_place || "", birth_date: (au.birth_date || "").slice(0, 10), nik: au.nik || "",
      spouse_name: au.spouse_name || "", employer: au.employer || "", monthly_income: au.monthly_income || "" });
    setAuEdit(au);
  };
  const saveAuEdit = () => api.put(`/ajl/app-users/${auEdit.id}`, auForm)
    .then((r) => { toast.success(r.data.message); setAuEdit(null); loadApp(); })
    .catch((e) => toast.error(errMsg(e)));
  const deleteAu = (au) => {
    if (!window.confirm(`Hapus data konsumen aplikasi "${au.name || au.email}"? Data di aplikasi AJL juga akan dihapus.`)) return;
    api.delete(`/ajl/app-users/${au.id}`)
      .then((r) => { toast.success(r.data.message); loadApp(); })
      .catch((e) => toast.error(errMsg(e)));
  };
  const findSpasiBooking = (au) => rows.find((r) => (r.account_email || "").toLowerCase() === (au.erp_email || au.email || "").toLowerCase());

  const openDetail = async (id) => {
    setLoadingDetail(true);
    try {
      const r = await api.get(`/konsumen/${id}`);
      setDetail(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoadingDetail(false); }
  };

  const filtered = rows.filter((r) => !q || (r.customer_name || "").toLowerCase().includes(q.toLowerCase()) || (r.number || "").toLowerCase().includes(q.toLowerCase()) || (r.unit_label || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="konsumen-page">
      <PageHeader title="Data Konsumen" subtitle="Data pembeli rumah — Super Admin dapat mengubah, menghapus, dan melengkapi berkas yang belum diunggah konsumen" testid="konsumen-header">
        <input className={`${inputCls} !w-56`} placeholder="Cari nama / no booking / unit..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="konsumen-search" />
      </PageHeader>
      <div className="flex gap-2 mb-4" data-testid="konsumen-tabs">
        {[["spasi", "Data Konsumen SPASI"], ["app", "Data Aplikasi ABAD JAYA LIVING"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`konsumen-tab-${k}`}
            className={`text-xs sm:text-sm px-3.5 py-2 rounded-lg border transition-colors ${tab === k ? "bg-emerald-600/10 text-emerald-300 border-emerald-500/50" : "text-slate-400 border-slate-800 hover:bg-slate-900"}`}>
            {l}
          </button>
        ))}
        {tab === "app" && (
          <Btn onClick={pullApp} disabled={pulling} className="ml-auto !py-2 text-xs" data-testid="appusers-pull-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${pulling ? "animate-spin" : ""}`} /> Tarik sekarang
          </Btn>
        )}
      </div>

      {tab === "app" && (
        <div className="space-y-2" data-testid="appusers-list">
          {appUsers.length === 0 && <Card><p className="text-slate-500 text-sm">Belum ada data pengguna aplikasi. Klik "Tarik sekarang" setelah URL aplikasi AJL dikonfigurasi.</p></Card>}
          {appUsers.map((au) => (
            <Card key={au.id} className="!p-4" testid={`appuser-${au.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Smartphone className="w-4 h-4 text-cyan-400 shrink-0" />
                <span className="font-medium text-slate-200 flex-1 min-w-[140px]">{au.name || au.email}</span>
                {au.matched_spasi ? <Badge tone="emerald">Tertaut SPASI</Badge> : <Badge tone="amber">Prospek Aplikasi</Badge>}
                {au.booking_ready ? <Badge tone="emerald">Siap Booking</Badge> : <Badge tone="rose">Belum Lengkap</Badge>}
                {au.deletion_requested && <Badge tone="rose">Minta Hapus Akun</Badge>}
              </div>
              <div className="text-[11px] text-slate-500 mt-1.5">
                {au.email || "-"} · {au.phone || "-"} · {au.occupation || "-"} · daftar {fmtDate((au.registered_at || "").slice(0, 10))}
              </div>
              {[...(au.profile_missing || []), ...(au.docs_missing || [])].length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {[...(au.profile_missing || []), ...(au.docs_missing || [])].map((m) => <Badge key={m} tone="rose">kurang: {m}</Badge>)}
                </div>
              )}
              {(au.bookings || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {au.bookings.map((b, i) => <Badge key={i} tone="cyan">{b.unit_label || b.unit_id} · {b.status}</Badge>)}
                </div>
              )}
              {!au.matched_spasi && (
                <Btn onClick={() => makeLead(au)} className="mt-2 !py-1.5 !px-3 text-xs" data-testid={`appuser-makelead-${au.id}`}>Jadikan Konsumen</Btn>
              )}
              <div className="flex gap-2 mt-2 flex-wrap">
                <Btn tone="ghost" className="!py-1.5 !px-3 text-xs" onClick={() => setAuDetail(au)} data-testid={`appuser-detail-${au.id}`}>Detail</Btn>
                {isSA && <Btn tone="ghost" className="!py-1.5 !px-3 text-xs" onClick={() => openAuEdit(au)} data-testid={`appuser-edit-${au.id}`}><Pencil className="w-3 h-3" /> Ubah</Btn>}
                {isSA && <Btn tone="danger" className="!py-1.5 !px-3 text-xs" onClick={() => deleteAu(au)} data-testid={`appuser-delete-${au.id}`}><Trash2 className="w-3 h-3" /> Hapus</Btn>}
                {au.matched_spasi && (au.docs_missing || []).length > 0 && (
                  <Btn className="!py-1.5 !px-3 text-xs" onClick={() => { const m = findSpasiBooking(au); if (m) openDetail(m.id); else toast.info("Buka kartu konsumen di tab Data Konsumen SPASI untuk melengkapi dokumen"); }} data-testid={`appuser-filldocs-${au.id}`}>
                    <Upload className="w-3 h-3" /> Lengkapi Dokumen ({(au.docs_missing || []).length})
                  </Btn>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={!!auDetail} onClose={() => setAuDetail(null)} title={`Konsumen Aplikasi — ${auDetail?.name || auDetail?.email || ""}`} testid="appuser-detail-modal">
        {auDetail && (
          <div className="space-y-3 text-sm max-h-[70vh] overflow-y-auto pr-1" data-testid="appuser-detail-content">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {[["Email", auDetail.email], ["Email ERP", auDetail.erp_email], ["No. HP", auDetail.phone],
                ["NIK", auDetail.nik], ["Tempat Lahir", auDetail.birth_place], ["Tanggal Lahir", (auDetail.birth_date || "").slice(0, 10)],
                ["Status Kawin", auDetail.marital_status], ["Nama Pasangan", auDetail.spouse_name],
                ["Jenis Pekerjaan", auDetail.job_type], ["Pekerjaan", auDetail.occupation],
                ["Instansi/Usaha", auDetail.employer], ["Penghasilan/Bulan", auDetail.monthly_income ? fmtIDR(auDetail.monthly_income) : null],
                ["Alamat", auDetail.address], ["Kota", auDetail.city],
                ["Kode Referral Miliknya", auDetail.referral_code_own],
                ["Daftar", fmtDate((auDetail.registered_at || "").slice(0, 10))], ["Aktivitas Terakhir", fmtDate((auDetail.last_activity_at || "").slice(0, 10))],
              ].map(([k, v]) => (
                <div key={k} className="py-1 border-b border-slate-800/60">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 block">{k}</span>
                  <span className="text-xs text-slate-200">{v || "-"}</span>
                </div>
              ))}
            </div>
            {auDetail.referred_by && (
              <div className="text-xs text-slate-400">Direferensikan oleh: <span className="text-slate-200">{auDetail.referred_by.referrer_name || auDetail.referred_by.code}</span> <Badge tone={auDetail.referred_by.status === "valid" ? "emerald" : "amber"}>{auDetail.referred_by.status}</Badge></div>
            )}
            {(auDetail.documents || []).length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">Dokumen di Aplikasi</div>
                {auDetail.documents.map((doc, i) => (
                  <div key={i} className="flex items-center gap-2 py-1 text-xs" data-testid={`appuser-doc-${i}`}>
                    <span className="text-slate-300 flex-1">{doc.name || doc.code}</span>
                    <Badge tone={doc.status === "terverifikasi" ? "emerald" : doc.status === "perlu_perbaikan" ? "rose" : "amber"}>{doc.status || "menunggu"}</Badge>
                    <span className="text-[10px] text-slate-500">{fmtDate((doc.uploaded_at || "").slice(0, 10))}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal open={!!auEdit} onClose={() => setAuEdit(null)} title={`Ubah Konsumen Aplikasi — ${auEdit?.name || ""}`} testid="appuser-edit-modal">
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nama"><input className={inputCls} value={auForm.name} onChange={(e) => setAuForm({ ...auForm, name: e.target.value })} data-testid="au-edit-name" /></Field>
            <Field label="No. HP"><input className={inputCls} value={auForm.phone} onChange={(e) => setAuForm({ ...auForm, phone: e.target.value })} data-testid="au-edit-phone" /></Field>
          </div>
          <Field label="Email"><input className={inputCls} value={auForm.email} onChange={(e) => setAuForm({ ...auForm, email: e.target.value })} data-testid="au-edit-email" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="NIK"><input className={inputCls} value={auForm.nik} onChange={(e) => setAuForm({ ...auForm, nik: e.target.value })} data-testid="au-edit-nik" /></Field>
            <Field label="Status Kawin">
              <select className={inputCls} value={auForm.marital_status} onChange={(e) => setAuForm({ ...auForm, marital_status: e.target.value })} data-testid="au-edit-marital">
                <option value="">—</option><option>Belum Menikah</option><option>Menikah</option><option>Cerai</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tempat Lahir"><input className={inputCls} value={auForm.birth_place} onChange={(e) => setAuForm({ ...auForm, birth_place: e.target.value })} data-testid="au-edit-birthplace" /></Field>
            <Field label="Tanggal Lahir"><input type="date" className={inputCls} value={auForm.birth_date} onChange={(e) => setAuForm({ ...auForm, birth_date: e.target.value })} data-testid="au-edit-birthdate" /></Field>
          </div>
          <Field label="Nama Pasangan"><input className={inputCls} value={auForm.spouse_name} onChange={(e) => setAuForm({ ...auForm, spouse_name: e.target.value })} data-testid="au-edit-spouse" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Jenis Pekerjaan"><input className={inputCls} value={auForm.job_type} onChange={(e) => setAuForm({ ...auForm, job_type: e.target.value })} data-testid="au-edit-jobtype" /></Field>
            <Field label="Pekerjaan"><input className={inputCls} value={auForm.occupation} onChange={(e) => setAuForm({ ...auForm, occupation: e.target.value })} data-testid="au-edit-occupation" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Instansi/Usaha"><input className={inputCls} value={auForm.employer} onChange={(e) => setAuForm({ ...auForm, employer: e.target.value })} data-testid="au-edit-employer" /></Field>
            <Field label="Penghasilan/Bulan (Rp)"><input type="number" className={inputCls} value={auForm.monthly_income} onChange={(e) => setAuForm({ ...auForm, monthly_income: e.target.value })} data-testid="au-edit-income" /></Field>
          </div>
          <Field label="Alamat"><textarea className={inputCls} rows={2} value={auForm.address} onChange={(e) => setAuForm({ ...auForm, address: e.target.value })} data-testid="au-edit-address" /></Field>
          <Field label="Kota"><input className={inputCls} value={auForm.city} onChange={(e) => setAuForm({ ...auForm, city: e.target.value })} data-testid="au-edit-city" /></Field>
          <Btn onClick={saveAuEdit} data-testid="au-edit-save">Simpan & Sinkron ke Aplikasi</Btn>
        </div>
      </Modal>

      {tab === "spasi" && (
      <div className="space-y-2" data-testid="konsumen-list">
        {filtered.length === 0 && <Card><p className="text-slate-500 text-sm">Belum ada data konsumen.</p></Card>}
        {filtered.map((r) => (
          <Card key={r.id} className="!p-4 cursor-pointer hover:border-emerald-500/40 transition-colors" testid={`konsumen-${r.id}`}>
            <button className="w-full text-left" onClick={() => openDetail(r.id)} data-testid={`konsumen-open-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <UserCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                <span className="font-medium text-slate-200 flex-1 min-w-[140px]">{r.customer_name}</span>
                <Badge tone="cyan">{r.unit_label}</Badge>
                <Badge tone="amber">{STAGE_LABEL[r.process_stage] || r.process_stage}</Badge>
                {r.has_account ? <Badge tone="emerald">Akun Aktif</Badge> : <Badge tone="slate">Belum Ada Akun</Badge>}
              </div>
              <div className="text-[11px] text-slate-500 mt-1.5 font-mono">{r.number} · berkas {r.berkas_count}/4 · {fmtDate((r.created_at || "").slice(0, 10))}</div>
            </button>
          </Card>
        ))}
      </div>
      )}

      <Modal open={!!detail || loadingDetail} onClose={() => setDetail(null)} title={detail ? `Konsumen — ${detail.booking?.customer_name}` : "Memuat..."} testid="konsumen-detail-modal">
        {detail && (
          <div className="space-y-4 text-sm" data-testid="konsumen-detail-content">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {[["No. Booking", detail.booking?.number], ["Unit", detail.unit ? `${detail.unit.block}-${detail.unit.number} (${detail.unit.type || "-"})` : "-"],
                ["Tahapan", STAGE_LABEL[detail.process_stage] || detail.process_stage],
                ["Email Akun", detail.account?.email || "Belum dibuat"],
                ["NIK", detail.form?.nik], ["Tempat Lahir", detail.form?.tempat_lahir],
                ["Tanggal Lahir", detail.form?.tanggal_lahir], ["No. HP", detail.form?.no_hp],
                ["Pekerjaan", detail.form?.pekerjaan], ["Alamat", detail.form?.alamat],
              ].map(([k, v]) => (
                <div key={k} className="py-1 border-b border-slate-800/60">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 block">{k}</span>
                  <span className="text-xs text-slate-200">{v || "-"}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">Berkas</div>
              {Object.entries(BERKAS_LABEL).map(([k, label]) => {
                const up = detail.berkas?.[k]?.uploaded;
                return (
                  <div key={k} className="flex items-center gap-2 py-1" data-testid={`konsumen-berkas-${k}`}>
                    {up ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Clock className="w-3.5 h-3.5 text-slate-600" />}
                    <span className={`text-xs ${up ? "text-slate-300" : "text-slate-500"}`}>{label}</span>
                    {up && <span className="text-[10px] font-mono text-slate-500 ml-auto">{fmtDate((detail.berkas[k].at || "").slice(0, 10))}</span>}
                  </div>
                );
              })}
            </div>
            {(detail.schedules || []).length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">Jadwal Pembayaran</div>
                {detail.schedules.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 py-1 text-xs" data-testid={`konsumen-schedule-${i}`}>
                    <span className="text-slate-300 flex-1">{s.label}</span>
                    <span className="font-mono text-slate-400">{fmtIDR(s.amount)}</span>
                    <Badge tone={s.status === "lunas" || (s.paid_amount || 0) >= s.amount ? "emerald" : (s.paid_amount || 0) > 0 ? "amber" : "slate"}>
                      {(s.paid_amount || 0) >= s.amount ? "Lunas" : (s.paid_amount || 0) > 0 ? `Sebagian (${fmtIDR(s.paid_amount)})` : "Belum"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
            {detail.berkas_app && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">Berkas Aplikasi (9 dokumen)</div>
                {Object.entries(BERKAS_APP_LABEL).map(([k, label]) => {
                  const b = detail.berkas_app[k] || {};
                  return (
                    <div key={k} className="flex items-center gap-2 py-1" data-testid={`konsumen-berkas-app-${k}`}>
                      {b.uploaded ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Clock className="w-3.5 h-3.5 text-slate-600" />}
                      <span className={`text-xs ${b.uploaded ? "text-slate-300" : "text-slate-500"}`}>{label}</span>
                      {b.required && <Badge tone="amber">wajib</Badge>}
                      {b.uploaded_by === "sales" && <Badge tone="cyan">Diunggah Sales</Badge>}
                      {b.uploaded_by === "admin" && <Badge tone="indigo">Diunggah Admin</Badge>}
                      {b.verified && <Badge tone="emerald">terverifikasi</Badge>}
                      {isSA && b.uploaded && (
                        <button onClick={() => deleteBerkas(detail.booking?.id, k)} className="ml-auto text-rose-400 hover:text-rose-300" data-testid={`konsumen-delberkas-${k}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                      {isSA && !b.uploaded && (
                        <label className="ml-auto text-[10px] text-emerald-300 cursor-pointer hover:underline" data-testid={`konsumen-upload-${k}`}>
                          <Upload className="w-3 h-3 inline" /> unggah
                          <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => uploadBerkas(detail.booking?.id, k, e.target.files[0])} />
                        </label>
                      )}
                      {!isSA && (k === "skp_desa" || k === "sku_desa") && !b.uploaded && (
                        <label className="ml-auto text-[10px] text-emerald-300 cursor-pointer hover:underline" data-testid={`konsumen-upload-sales-${k}`}>
                          <Upload className="w-3 h-3 inline" /> unggah
                          <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => uploadBerkas(detail.booking?.id, k, e.target.files[0])} />
                        </label>
                      )}
                    </div>
                  );
                })}
                {!detail.berkas_app.rekening_koran?.required && (
                  <Btn onClick={() => requireRK(detail.booking?.id)} className="mt-2 !py-1.5 !px-3 text-xs" data-testid="konsumen-require-rk">
                    <Landmark className="w-3.5 h-3.5" /> Bank meminta Rekening Koran
                  </Btn>
                )}
              </div>
            )}
            <div className="flex justify-between items-center">
              {isSA && (
                <div className="flex gap-2">
                  <Btn tone="ghost" className="!py-1.5 !px-3 text-xs" onClick={openEdit} data-testid="konsumen-edit-btn"><Pencil className="w-3.5 h-3.5" /> Ubah Data</Btn>
                  <Btn tone="danger" className="!py-1.5 !px-3 text-xs" onClick={deleteKonsumen} data-testid="konsumen-delete-btn"><Trash2 className="w-3.5 h-3.5" /> Hapus</Btn>
                </div>
              )}
              <Btn tone="ghost" className={isSA ? "" : "ml-auto"} onClick={() => setDetail(null)}>Tutup</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title={`Ubah Data — ${editForm.customer_name || ""}`} testid="konsumen-edit-modal">
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <Field label="Nama Konsumen"><input className={inputCls} value={editForm.customer_name || ""} onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })} data-testid="edit-name-input" /></Field>
          <Field label="No. HP Akun"><input className={inputCls} value={editForm.customer_phone || ""} onChange={(e) => setEditForm({ ...editForm, customer_phone: e.target.value })} data-testid="edit-phone-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="NIK"><input className={inputCls} value={editForm.nik || ""} onChange={(e) => setEditForm({ ...editForm, nik: e.target.value })} data-testid="edit-nik-input" /></Field>
            <Field label="No. HP Formulir"><input className={inputCls} value={editForm.no_hp || ""} onChange={(e) => setEditForm({ ...editForm, no_hp: e.target.value })} data-testid="edit-nohp-input" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tempat Lahir"><input className={inputCls} value={editForm.tempat_lahir || ""} onChange={(e) => setEditForm({ ...editForm, tempat_lahir: e.target.value })} data-testid="edit-tempat-input" /></Field>
            <Field label="Tanggal Lahir"><input className={inputCls} placeholder="DD-MM-YYYY" value={editForm.tanggal_lahir || ""} onChange={(e) => setEditForm({ ...editForm, tanggal_lahir: e.target.value })} data-testid="edit-tanggal-input" /></Field>
          </div>
          <Field label="Pekerjaan"><input className={inputCls} value={editForm.pekerjaan || ""} onChange={(e) => setEditForm({ ...editForm, pekerjaan: e.target.value })} data-testid="edit-pekerjaan-input" /></Field>
          <Field label="Alamat"><textarea className={inputCls} rows={2} value={editForm.alamat || ""} onChange={(e) => setEditForm({ ...editForm, alamat: e.target.value })} data-testid="edit-alamat-input" /></Field>
          <Btn onClick={saveEdit} data-testid="konsumen-save-btn">Simpan Perubahan</Btn>
        </div>
      </Modal>
    </div>
  );
}
